import { useMemo, useRef, useState } from "react";
import { useComment } from "../../contexts/CommentContext";
import { Button } from "../../design/button";
import CommentListItem from "../CommentListItem/CommentListItem";
import SlateTextArea, { SlateEditorRef } from "../SlateTextArea/SlateTextArea";
import { Descendant } from "slate";
import { getPlainText } from "../../utils/slate";

interface CommentListProps {
  fileId?: string;
  eventId?: string;
  participantId?: string;
  documentId?: string;
  discoveryRequestId?: string;
  taskId?: string;
  subtaskId?: string;
  childSupportOrderId?: string;
  childSupportPaymentId?: string;
}

const CommentList = ({
  fileId,
  eventId,
  participantId,
  documentId,
  discoveryRequestId,
  taskId,
  subtaskId,
  childSupportOrderId,
  childSupportPaymentId,
}: CommentListProps) => {
  const editorRef = useRef<SlateEditorRef | null>(null);
  const { createComment, comments } = useComment();
  const [comment, setComment] = useState<Descendant[] | null>(null);

  const handleAddComment = async () => {
    try {
      await createComment({
        content: JSON.stringify(comment),
        parentType:
          (fileId && "File") ||
          (eventId && "Event") ||
          (participantId && "Participant") ||
          (documentId && "Document") ||
          (discoveryRequestId && "DiscoveryRequest") ||
          (taskId && "Task") ||
          (subtaskId && "Subtask") ||
          (childSupportOrderId && "ChildSupportOrder") ||
          "ChildSupportPayment",
        parent:
          fileId ||
          eventId ||
          participantId ||
          documentId ||
          discoveryRequestId ||
          taskId ||
          subtaskId!,
        comments: [],
      });

      setComment(null);
      editorRef.current?.resetEditor();
    } catch (error) {
      console.error("Error adding comment:", error);
    }
  };

  const filteredComments = useMemo(() => {
    if (!comments) return [];

    return comments.filter((comment) => {
      if (fileId && comment.parent !== fileId) return false;
      if (eventId && comment.parent !== eventId) return false;
      if (participantId && comment.parent !== participantId) return false;
      if (documentId && comment.parent !== documentId) return false;
      if (discoveryRequestId && comment.parent !== discoveryRequestId)
        return false;
      if (taskId && comment.parent !== taskId) return false;
      if (subtaskId && comment.parent !== subtaskId) return false;
      if (childSupportOrderId && comment.parent !== childSupportOrderId)
        return false;
      if (childSupportPaymentId && comment.parent !== childSupportPaymentId)
        return false;
      return true;
    });
  }, [
    comments,
    fileId,
    eventId,
    participantId,
    documentId,
    discoveryRequestId,
    taskId,
    subtaskId,
    childSupportOrderId,
    childSupportPaymentId,
  ]);

  return (
    <div>
      <div className="my-4">
        <SlateTextArea
          id="comment"
          ref={editorRef}
          initialValue={comment ?? undefined}
          onChange={setComment}
        />
        <Button
          type="button"
          className="w-full py-2 mt-2 text-white bg-blue-500 rounded-md"
          onClick={handleAddComment}
          disabled={getPlainText(comment ?? []) === ""}
        >
          Add Comment
        </Button>
      </div>
      <div>
        {filteredComments?.map((comment) => (
          <CommentListItem key={comment._id} comment={comment} />
        ))}
      </div>
    </div>
  );
};

export default CommentList;
